"use client";

import UserAvatar from "@/components/Avatar/UserAvatar";
import { useRouter, Link } from "@/i18n/navigation";
import { useState } from "react";
import { useSession } from "next-auth/react";
import Breadcrumb from "@/components/ui/Breadcrumb/Breadcrumb";
import Message from "../../Course/Message/Message";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import Button from "../../ui/Button/Button";
import Image from "next/image";
import Prism from "prismjs";
import { Editor } from "@tinymce/tinymce-react";
import { getDiscussionLocation } from "@/helpers/bestAnswer";
import { useLocale, useTranslations } from "next-intl";

export default function CommunityDiscussionComponent({
    discussion: tmpDiscussion,
    id,
    pureId,
}) {
    const locale = useLocale();
    const t = useTranslations();

    const router = useRouter();
    const { data: session } = useSession();

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [editorKey, setEditorKey] = useState(1);
    const [discussion, setDiscussion] = useState(tmpDiscussion);
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [discussionIsDeleting, setDiscussionIsDeleting] = useState(false);
    const [bestMessageIsUpdating, setBestMessageIsUpdating] = useState(false);
    const [title, setTitle] = useState(tmpDiscussion.title);
    const [discussionIsEditing, setDiscussionIsEditing] = useState(false);

    // Methods
    const clearEditor = () => {
        const key = editorKey + 1;
        setEditorKey(key);
    };
    const sendReply = async () => {
        // If message is empty
        if (message.trim() == "") {
            toast.error(t("CommunityDiscussion_error"));
            return;
        }

        if (!isSending) {
            setIsSending(true);

            const response = await fetch("/api/interface/post/answer", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    discussionId: discussion.id,
                    content: message,
                    ...getDiscussionLocation(discussion),
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsSending(false);
                toast.error(fetchedData.message);
            } else {
                setDiscussion(fetchedData.discussion);
                setIsSending(false);
                setMessage("");
                clearEditor();
                Prism.highlightAll();
                toast.success(t("CommunityDiscussion_success"));
            }
        }
    };
    const deleteMessageHandler = async (messageId, isDiscussion = false) => {
        if (!discussionIsDeleting) {
            setDiscussionIsDeleting(true);

            if (isDiscussion) {
                // Send confirm box
                const confirmed = confirm(
                    t("CommunityDiscussion_confirmDeleteDiscussion"),
                );

                if (confirmed) {
                    const response = await fetch(
                        "/api/interface/delete/discussion",
                        {
                            method: "post",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                messageId,
                            }),
                        },
                    );

                    const fetchedData = await response.json();

                    if (!response.ok) {
                        setDiscussionIsDeleting(false);
                        toast.error(fetchedData.message);
                    } else {
                        setDiscussionIsDeleting(false);
                        router.push("/community");
                        toast.success(
                            t("CommunityDiscussion_discussionDeleted"),
                        );
                    }
                } else {
                    setDiscussionIsDeleting(false);
                }
            } else {
                // Send confirm box
                const confirmed = confirm(
                    t("CommunityDiscussion_confirmDelete"),
                );

                if (confirmed) {
                    const response = await fetch(
                        "/api/interface/delete/message",
                        {
                            method: "post",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                messageId,
                            }),
                        },
                    );

                    const fetchedData = await response.json();

                    if (!response.ok) {
                        setDiscussionIsDeleting(false);
                        toast.error(fetchedData.message);
                    } else {
                        // Remove message from discussion
                        const newDiscussion = discussion;
                        newDiscussion.messages = newDiscussion.messages.filter(
                            (message) => message.id != messageId,
                        );
                        setDiscussion(newDiscussion);
                        setDiscussionIsDeleting(false);
                        toast.success(t("CommunityDiscussion_messageDeleted"));
                    }
                } else {
                    setDiscussionIsDeleting(false);
                }
            }
        }
    };
    const markGuideHandler = async (discussionId) => {
        if (!bestMessageIsUpdating) {
            setBestMessageIsUpdating(true);

            const response = await fetch("/api/interface/post/guide", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    discussionId,
                    ...getDiscussionLocation(discussion),
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setBestMessageIsUpdating(false);
                toast.error(fetchedData.message);
            } else {
                // Update discussion
                setDiscussion(fetchedData.discussion);
                setBestMessageIsUpdating(false);
                Prism.highlightAll();
                toast.success(
                    discussion.isForHelp
                        ? t("CommunityDiscussion_notMarkedForGuide")
                        : t("CommunityDiscussion_markedForGuide"),
                );
            }
        }
    };

    return (
        <>
            {/* Head */}
            <section className="mb-8">
                {/* Infos */}
                <Breadcrumb
                    links={[
                        {
                            link: "/community",
                            name: t("community_title"),
                        },
                        {
                            link: `/community/discussions/${id}`,
                            name: discussion.title,
                        },
                    ]}
                />
                <div className="mt-6">
                    <h1 className="md:text-4xl text-3xl dark:text-white font-semibold tracking-tight">
                        {discussion.title}
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap mt-5">
                        {discussion.messages.filter(
                            (message) => message.bestMessage,
                        ).length > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-1 ring-emerald-600/10 dark:ring-emerald-500/20">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                    className="w-3.5 h-3.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4.5 12.75l6 6 9-13.5"
                                    />
                                </svg>
                                {t("CommunityDiscussion_bestMessage")}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-grayDark-400">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {moment
                                .utc(discussion.creationDate)
                                .tz(
                                    session
                                        ? session.user.timezone
                                        : "Europe/Paris",
                                )
                                .locale(locale)
                                .format("LLL")}
                        </span>
                        {discussion.lastActivity && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-grayDark-400">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                                {moment
                                    .utc(discussion.lastActivity)
                                    .tz(
                                        session
                                            ? session.user.timezone
                                            : "Europe/Paris",
                                    )
                                    .locale(locale)
                                    .format("LLL")}
                            </span>
                        )}
                        {discussion.isForHelp && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 ring-1 ring-amber-600/10 dark:ring-amber-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                                </svg>
                                {t("CommunityDiscussion_guide")}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            <section className="flex md:flex-row flex-col gap-8 py-8">
                {/* List */}
                <div className="lg:w-9/12 md:w-8/12 flex flex-col md:order-1 order-2 gap-6">
                    {/* Messages container */}
                    <div className="bg-white dark:bg-white/[0.02] rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm">
                        <Message
                            message={discussion}
                            session={session ? session : null}
                            deleteMessage={() =>
                                deleteMessageHandler(discussion.id, true)
                            }
                            discussionIsDeleting={discussionIsDeleting}
                            markGuide={() => markGuideHandler(discussion.id)}
                            title={title}
                            setTitle={setTitle}
                            setDiscussionIsEditing={setDiscussionIsEditing}
                        />

                        <div className="flex flex-col divide-y divide-gray-100 dark:divide-white/5">
                            {discussion.messages.map((message) => (
                                <Message
                                    key={message.id}
                                    message={message}
                                    session={session ? session : null}
                                    deleteMessage={() =>
                                        deleteMessageHandler(message.id)
                                    }
                                    discussionIsDeleting={discussionIsDeleting}
                                    discussion={discussion}
                                    setDiscussion={setDiscussion}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Reply editor */}
                    <div className="bg-white dark:bg-white/[0.02] rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300/80 dark:hover:border-white/15">
                        {session ? (
                            <div className="p-6">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="rounded-full w-12 h-12 ring-2 ring-gray-100 dark:ring-white/10 overflow-hidden">
                                            <UserAvatar
                                                user={session.user}
                                                alt="Image"
                                                width={48}
                                                height={48}
                                                className="rounded-full object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div className="w-full dynamic-content min-w-0">
                                        <Editor
                                            key={editorKey}
                                            tinymceScriptSrc="/tinymce/tinymce.min.js"
                                            licenseKey="gpl"
                                            init={{
                                                relative_urls: false,
                                                remove_script_host: false,
                                                menubar: false,
                                                inline: true,
                                                codesample_languages:
                                                    process.env
                                                        .TINYMCE_CODESAMPLE_LANGUAGES,
                                                codesample_global_prismjs: true,
                                                toolbar_location: "bottom",
                                                fixed_toolbar_container:
                                                    "#editor-toolbar",
                                                plugins:
                                                    "codesample anchor autolink link autoresize autosave fullscreen image lists preview searchreplace table visualblocks wordcount",
                                                toolbar:
                                                    "undo redo | bold italic forecolor | numlist bullist | image codesample | link anchor | restoredraft",
                                                content_style: session.user
                                                    .darkMode
                                                    ? ".mce-content-body {outline: none; margin: 0; max-height: 80vh; overflow: auto; color: white; border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 14px; background: rgba(255,255,255,0.02); transition: border-color 0.2s ease} .mce-content-body:focus {border-color: rgba(255,255,255,0.2)} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #6b7280; padding-left: 14px } .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid rgba(255,255,255,0.1)!important }"
                                                    : ".mce-content-body {outline: none; margin: 0; max-height: 80vh; overflow: auto; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; background: #f9fafb; transition: border-color 0.2s ease, background 0.2s ease} .mce-content-body:focus {border-color: #d1d5db; background: white} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #9ca3af; padding-left: 14px } .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid #e5e7eb!important }",
                                                placeholder: t(
                                                    "Message_writeYourMessage",
                                                ),
                                                skin: session.user.darkMode
                                                    ? "oxide-dark"
                                                    : "oxide",
                                                content_css: session.user.darkMode
                                                    ? false
                                                    : "default",
                                                image_dimensions: false,
                                                automatic_uploads: false,
                                                image_title: false,
                                                image_description: false,
                                                file_picker_types: "image",
                                                file_picker_callback: (callback, value, meta) => {
                                                    if (meta.filetype === "image") {
                                                        const input = document.createElement("input");
                                                        input.setAttribute("type", "file");
                                                        input.setAttribute("accept", "image/*");

                                                        input.onchange = async function () {
                                                            const file = this.files[0];
                                                            if (!file) return;

                                                            const formData = new FormData();
                                                            formData.append("file", file);

                                                            try {
                                                                const response = await fetch("/api/upload/image", {
                                                                    method: "POST",
                                                                    body: formData,
                                                                });

                                                                if (!response.ok) {
                                                                    const error = await response.json();
                                                                    throw new Error(error.message || "Erreur lors de l'upload");
                                                                }

                                                                const data = await response.json();

                                                                const altText = file.name
                                                                    .replace(/\.[^/.]+$/, "")
                                                                    .replace(/[-_]/g, " ");

                                                                const editor = tinymce.activeEditor;
                                                                editor.insertContent(
                                                                    `<img src="${data.location}" alt="${altText}" style="margin-bottom: 12px; max-width: 100%; height: auto;" />`
                                                                );

                                                                editor.windowManager.close();
                                                            } catch (error) {
                                                                console.error("Erreur upload:", error);
                                                                alert("Erreur lors de l'upload de l'image");
                                                            }
                                                        };

                                                        input.click();
                                                    }
                                                },
                                            }}
                                            onChange={(e) => {
                                                setMessage(
                                                    e.target.getContent(),
                                                );
                                            }}
                                        />
                                        <div
                                            className="editor-toolbar mt-3"
                                            id="editor-toolbar"
                                        ></div>
                                    </div>
                                </div>
                                <div className="flex justify-end mt-5 pt-4 border-t border-gray-100 dark:border-white/5">
                                    <Button
                                        onClick={sendReply}
                                        isLoading={isSending}
                                        progress
                                    >
                                        {t("CommunityDiscussion_send")}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex gap-4 items-center p-6">
                                <div className="flex-shrink-0">
                                    <div className="rounded-full w-11 h-11 overflow-hidden ring-2 ring-gray-100 dark:ring-white/10 relative">
                                        <Image
                                            src="https://blvstatic.com/pictures/default.png"
                                            alt="Image"
                                            className="rounded-full object-cover"
                                            fill
                                        />
                                    </div>
                                </div>
                                <div className="text-gray-600 dark:text-grayDark-400 text-sm">
                                    <Link href="/signup" className="text-primary hover:underline font-medium">
                                        {t("CommunityDiscussion_signup")}
                                    </Link>{" "}
                                    {t("CommunityDiscussion_or").toLowerCase()}{" "}
                                    <Link href="/login" className="text-primary hover:underline font-medium">
                                        {t("CommunityDiscussion_login")}
                                    </Link>{" "}
                                    {t("CommunityDiscussion_toReply").toLowerCase()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Sidebar */}
                <div className="lg:w-3/12 md:w-4/12 md:order-1 order-2">
                    <div className="sticky top-20">
                        {discussion &&
                        discussion.session &&
                        discussion.session.part &&
                        discussion.session.part.course &&
                        discussion.session.part.course.category &&
                        discussion.session.part.course.slug &&
                        discussion.session.part.course.socialImage &&
                        discussion.session.part.course.category == "course" ? (
                            <Link
                                className="block group"
                                href={{
                                    pathname: "/course/[slug]",
                                    params: { slug: discussion.session.part.course.slug },
                                }}
                            >
                                <div className="bg-white dark:bg-white/[0.02] rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm overflow-hidden transition-all duration-300 group-hover:shadow-lg group-hover:border-gray-300/80 dark:group-hover:border-white/15 group-hover:-translate-y-0.5">
                                    <div className="w-full aspect-[16/9] relative overflow-hidden">
                                        <Image
                                            src={
                                                discussion.session.part.course
                                                    .socialImage
                                            }
                                            alt={
                                                discussion.session.part.course
                                                    .name
                                            }
                                            fill
                                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                    <div className="p-5">
                                        <p className="text-xs font-medium text-gray-500 dark:text-grayDark-400 uppercase tracking-wide mb-2">
                                            {t("CommunityDiscussion_course")}
                                        </p>
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-snug mb-4">
                                            {discussion.session.part.course.name}
                                        </h3>
                                        <Button large>
                                            {t("CommunityDiscussion_seeCourse")}
                                        </Button>
                                    </div>
                                </div>
                            </Link>
                        ) : (
                            <div className="bg-white dark:bg-white/[0.02] rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500 dark:text-grayDark-400">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-600 dark:text-grayDark-400 text-sm">
                                        {t("CommunityDiscussion_noCourse")}
                                    </p>
                                </div>
                                <Link href="/accelerators">
                                    <Button large>
                                        {t("CommunityDiscussion_seeAccelerators")}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
